import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';

import { Input } from '@/components/ui/inputs/input';
import { InputPassWord } from '@/components/ui/inputs/input-password';

import {
  loginAuthService,
  customerLoginAuthService,
  getGeneralSettingStoreAfterLoginService,
} from '@/services/AuthServices';
import SystemService from '@/services/SystemService';
import ToastMe from '@/components/ui/ToastMe';

import validationErrors from '@/lib/validationErrors';
import { GeneralSettingList } from '@/constants/GeneralSettingConstant';
import Moment from 'moment';

import logoImage from '@/assets/images/logo.png';

export const FORM_FONT_COLOR = 'rgba(255, 193, 7, 1)';
const BRAND = {
  primary: '#156372',
  yellow: 'rgba(255, 193, 7, 1)',
  ink: '#0F2F35',
};

const DEBUG_DEFAULT = true;
function isDebugOn() {
  try {
    return (
      (typeof window !== 'undefined' && !!window.__DEBUG__) || DEBUG_DEFAULT
    );
  } catch {
    return DEBUG_DEFAULT;
  }
}

// 🔥 unified yellow style for inputs (text, placeholder, caret)
const YELLOW_INPUT_CLASS =
  'text-[rgba(255,193,7,1)] placeholder:text-[rgba(255,193,7,0.75)] caret-[rgba(255,193,7,1)]';

const LoginPage = () => {
  const { isAuthenticated, userData } = useSelector((s) => s?.Auth);
  const { todayDateUpdate } = useSelector((s) => s?.GeneralSetting);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [isAdminLogin] = useState(true);
  const [licenseKey, setLicenseKey] = useState('');
  const [isRegisteringLicense, setIsRegisteringLicense] = useState(false);
  const [companyChoices, setCompanyChoices] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [pendingLoginData, setPendingLoginData] = useState(null);

  useEffect(() => {
    try {
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        window.location.replace(
          `https://${window.location.host}${window.location.pathname}${window.location.search}${window.location.hash}`
        );
        return;
      }
    } catch {}

    try {
      const params = new URLSearchParams(window.location.search || '');
      const keyFromUrl = params.get('license') || params.get('accessKey') || '';
      if (keyFromUrl) setLicenseKey(keyFromUrl);

      const e = localStorage.getItem('fp_email') || '';
      const t = localStorage.getItem('fp_token') || '';
      if (!isAuthenticated && e) {
        navigate(
          t
            ? `/reset-password?email=${encodeURIComponent(
                e
              )}&token=${encodeURIComponent(t)}`
            : `/forget-password/verify?email=${encodeURIComponent(e)}`,
          { replace: true }
        );
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated && userData?.id) navigate(`/admin/dashboard`);
    setValue('email', '');
    if (todayDateUpdate === Moment.utc().format('DD/MM/YYYY')) {
      dispatch(
        getGeneralSettingStoreAfterLoginService({ key_list: GeneralSettingList })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data) => {
    setIsLoading(true);
    const loginService = isAdminLogin
      ? loginAuthService
      : customerLoginAuthService;

    if (isDebugOn()) {
      const preview = { ...data };
      if (preview.password) preview.password = '••••••';
      console.groupCollapsed('[LoginPage] submit');
      console.log('payload:', preview);
      console.log('isAdminLogin:', isAdminLogin);
      console.groupEnd();
    }

    try {
      const result = await dispatch(
        loginService({ ...data, isAdmin: isAdminLogin })
      );
      if (isDebugOn()) console.log('[LoginPage] dispatch result:', result);
      setCompanyChoices([]);
      setSelectedCompanyId('');
      setPendingLoginData(null);
      reset();
      navigate(isAdminLogin ? '/admin/dashboard' : '/customer/dashboard');
    } catch (e) {
      if (isDebugOn()) console.warn('[LoginPage] login failed:', e);
      if (e?.code === 'company_selection_required') {
        const companies = Array.isArray(e?.data?.companies) ? e.data.companies : [];
        setPendingLoginData(data);
        setCompanyChoices(companies);
        setSelectedCompanyId(companies[0]?.company_id || '');
        return;
      }
      const { message, errorData } = e || {};
      validationErrors(errorData, message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueWithCompany = async () => {
    const values = pendingLoginData || getValues();
    if (!selectedCompanyId) {
      ToastMe('Choose a company to continue.', 'error');
      return;
    }

    setIsLoading(true);
    try {
      await dispatch(
        loginAuthService({
          ...values,
          company_id: selectedCompanyId,
          isAdmin: true,
        })
      );
      setCompanyChoices([]);
      setSelectedCompanyId('');
      setPendingLoginData(null);
      reset();
      navigate('/admin/dashboard');
    } catch (e) {
      const { message, errorData } = e || {};
      validationErrors(errorData, message);
    } finally {
      setIsLoading(false);
    }
  };

  const redirectToCustomerLogin = () => navigate('/sign-in-customer');

  const handleLicenseRegister = async (event) => {
    event.preventDefault();
    const accessKey = licenseKey.trim();
    if (!accessKey) {
      ToastMe('License key is required.', 'error');
      return;
    }

    setIsRegisteringLicense(true);
    try {
      const response = await SystemService.registerLicense({ accessKey });
      ToastMe(response?.message || 'License registered successfully.', 'success');
      navigate('/setup', { replace: true });
    } catch (error) {
      ToastMe(
        error?.response?.data?.message ||
          error?.message ||
          'License registration failed.',
        'error'
      );
    } finally {
      setIsRegisteringLicense(false);
    }
  };

  return (
    <section
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: BRAND.yellow }}
    >
      <div className="mx-auto max-w-5xl px-4 py-10 w-full">
        <div className="relative z-10 grid gap-6 lg:grid-cols-[0.95fr_1.05fr] items-stretch">
              <div
                className="relative rounded-3xl shadow-2xl ring-1 ring-black/5 overflow-hidden"
                style={{ backgroundColor: '#FFFFFF' }}
              >
                <div
                  className="absolute inset-y-0 left-0 w-1"
                  style={{ backgroundColor: BRAND.primary }}
                />
                <div className="p-6 md:p-10 h-full flex flex-col justify-center">
                  <div className="mb-6">
                    <img
                      src={logoImage}
                      alt="Company logo"
                      className="h-14 w-auto mb-5"
                    />
                    <h2
                      className="text-2xl md:text-3xl font-semibold tracking-tight"
                      style={{ color: BRAND.ink }}
                    >
                      Register License
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                      Enter the license key from Taban website to activate this
                      workspace and continue company setup.
                    </p>
                  </div>

                  <form onSubmit={handleLicenseRegister} className="space-y-4">
                    <div>
                      <label
                        htmlFor="licenseKey"
                        className="block text-sm font-semibold mb-2"
                        style={{ color: BRAND.ink }}
                      >
                        License key
                      </label>
                      <input
                        id="licenseKey"
                        type="text"
                        value={licenseKey}
                        onChange={(event) => setLicenseKey(event.target.value)}
                        placeholder="Paste your Taban Foods license"
                        autoComplete="off"
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#156372]/30 focus:border-[#156372]"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isRegisteringLicense}
                      className="w-full py-3 rounded-xl font-semibold shadow focus:outline-none focus:ring-2 hover:brightness-95 active:brightness-90 disabled:opacity-80 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: BRAND.primary,
                        color: '#FFFFFF',
                        boxShadow: '0 12px 30px rgba(21,99,114,.25)',
                      }}
                    >
                      {isRegisteringLicense ? 'Registering…' : 'Register License'}
                    </button>
                  </form>

                  <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                    After the license is accepted, setup will ask for the
                    company details and first admin user.
                  </div>
                </div>
              </div>

              <div
                className="relative rounded-3xl shadow-2xl ring-1 ring-black/5 overflow-hidden"
                style={{ backgroundColor: BRAND.primary }}
              >
                <div
                  className="absolute inset-y-0 left-0 w-1"
                  style={{ backgroundColor: BRAND.yellow }}
                />
                <div className="p-6 md:p-10">
                  <div className="flex flex-col items-center mb-6">
                    <img
                      src={logoImage}
                      alt="Company logo"
                      className="h-16 w-auto mb-3"
                    />
                    <h1
                      className="text-3xl md:text-4xl font-semibold text-center tracking-tight"
                      style={{ color: '#FFFFFF' }}
                    >
                      Welcome Back
                    </h1>
                    <p className="mt-2 text-sm text-white/80 text-center">
                      Admin access to manage the platform
                    </p>
                  </div>

                  {Object.keys(errors || {}).length > 0 && (
                    <div className="mb-3 rounded-md bg-red-50 text-red-700 text-sm px-3 py-2">
                      Please fix the highlighted fields below.
                    </div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {/* EMAIL */}
                    <Controller
                      control={control}
                      name="email"
                      rules={{
                        required: true,
                        pattern: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/,
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          autoFocus
                          type="email"
                          labelText="Email"
                          placeholder="Enter your email"
                          errorType={errors?.email?.type}
                          groupClassName="mb-1"
                          // 🔥 yellow text + placeholder
                          inputClassName={YELLOW_INPUT_CLASS}
                        />
                      )}
                    />

                    {/* PASSWORD */}
                    <Controller
                      control={control}
                      name="password"
                      rules={{ required: true, minLength: 6, maxLength: 20 }}
                      render={({ field }) => (
                        <InputPassWord
                          {...field}
                          type="password"
                          labelText="Password"
                          placeholder="Enter your password"
                          autoComplete="off"
                          errorData={{ minLength: '6', maxLength: '20' }}
                          errorType={errors?.password?.type}
                          // 🔥 yellow text + placeholder
                          inputClassName={YELLOW_INPUT_CLASS}
                        />
                      )}
                    />

                    {!!companyChoices.length && (
                      <div className="rounded-xl border border-white/20 bg-white/10 p-4">
                        <p className="text-sm font-medium text-white">
                          This user belongs to multiple companies. Choose which company to open.
                        </p>
                        <select
                          value={selectedCompanyId}
                          onChange={(event) => setSelectedCompanyId(event.target.value)}
                          className="mt-3 w-full rounded-xl border border-white/20 bg-white px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-white/40"
                        >
                          {companyChoices.map((company) => (
                            <option key={company.company_id} value={company.company_id}>
                              {company.company_name} ({company.company_code || company.company_id})
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleContinueWithCompany}
                          disabled={isLoading || !selectedCompanyId}
                          className="mt-3 w-full py-3 rounded-xl font-semibold shadow focus:outline-none focus:ring-2 hover:brightness-95 active:brightness-90 disabled:opacity-80 disabled:cursor-not-allowed"
                          style={{
                            backgroundColor: '#FFFFFF',
                            color: BRAND.primary,
                          }}
                        >
                          {isLoading ? 'Opening company…' : 'Continue With Selected Company'}
                        </button>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-sm">
                      <label
                        htmlFor="remember"
                        className="inline-flex items-center gap-2 select-none"
                      >
                        <input
                          id="remember"
                          type="checkbox"
                          className="h-4 w-4 rounded"
                          style={{ accentColor: '#FFFFFF' }}
                        />
                        <span className="text-white/90">Remember Me</span>
                      </label>

                      <Link
                        to="/forget-password"
                        className="font-medium hover:underline"
                        style={{ color: BRAND.yellow }}
                      >
                        Forgot Password
                      </Link>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 rounded-xl font-semibold shadow focus:outline-none focus:ring-2 hover:brightness-95 active:brightness-90 disabled:opacity-80 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: BRAND.yellow,
                        color: BRAND.ink,
                        boxShadow: '0 12px 30px rgba(0,0,0,.25)',
                      }}
                    >
                      {isLoading ? 'Logging in…' : 'Log In'}
                    </button>

                    <div className="text-center pt-2">
                      <button
                        type="button"
                        onClick={redirectToCustomerLogin}
                        className="text-sm font-medium hover:underline"
                        style={{ color: BRAND.yellow }}
                      >
                        Login as Customer
                      </button>
                    </div>

                    <div className="pt-4 text-center text-[11px] text-white/80">
                      Secure login • TLS protected
                    </div>
                  </form>
                </div>
              </div>
        </div>
      </div>
    </section>
  );
};

export default LoginPage;
